import {
  Commercial,
  Edit,
  EditReview,
  EditReviewAnalysis,
  EditReviewService,
  PerformerContract,
  RolePerformerType,
  TEditReviewPerformerType,
} from '@talon/web/lib/api';
import { Button, CardBody, CardHeader, Layer } from 'grommet';
import { Magic, Update } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useCommercialStore, usePerformerStore } from '../../../../context';
import { Loader } from '../../Loader';
import { ModalCard } from '../../ModalCard';
import { ContractedPerformersPanel } from './ContractedPerformersPanel';
import { EnlargedFrameModal } from './EnlargedFrameModal';
import { MergeDialogModal } from './MergeDialogModal';
import { UnknownPerformersPanel } from './UnknownPerformersPanel';

export interface IEditReviewModalProps {
  commercial: Commercial;
  edit: Edit;
  setShowEditReviewModal: (isVisible: boolean) => void;
}

export enum Verdict {
  upgrade = 'Upgrade',
  downgrade = 'Downgrade',
  noChange = 'No Change',
  outgraded = 'Manually Outgraded',
}

export type KnownAnalyses = {
  contract: PerformerContract;
  analysis?: EditReviewAnalysis;
  frameUrls?: string[];
  verdict: Verdict;
}[];

export type UnknownAnalyses = { analysis: EditReviewAnalysis; frameUrls?: string[] }[];

export const EditReviewModal = observer(({ commercial, edit, setShowEditReviewModal }: IEditReviewModalProps) => {
  /** Context **/
  const commercialStore = useCommercialStore();
  const performerStore = usePerformerStore();

  /** State **/
  const [knownAnalyses, setKnownAnalyses] = useState<
    { contract: PerformerContract; analysis?: EditReviewAnalysis; frameUrls?: string[]; verdict: Verdict }[]
  >([]);
  const [unknownAnalyses, setUnknownAnalyses] = useState<{ analysis: EditReviewAnalysis; frameUrls?: string[] }[]>([]);
  const [contracts, setContracts] = useState<PerformerContract[]>([]);
  const [editReview, setEditReview] = useState<EditReview | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [processingAction, setProcessingAction] = useState<string | null>(null);
  const [selectedPerformers, setSelectedPerformers] = useState<Set<string>>(new Set());
  const [showMergeDialog, setShowMergeDialog] = useState<boolean>(false);
  const [mergeName, setMergeName] = useState<string>('');
  const [enlargedFrameUrl, setEnlargedFrameUrl] = useState<string>('');

  /** Methods **/

  const createEditReview = async () => {
    if (edit) {
      setLoading(true);
      const response = await commercialStore.createEditReview(edit.id);
      if (response) {
        setEditReview(response.editReview);
      }
      setLoading(false);
    }
  };

  const handleReclassify = async (editId: string, performerId: string, newType: RolePerformerType, reason?: string) => {
    if (!editId && !edit.id) return;

    setProcessingAction(performerId);
    try {
      const response = await EditReviewService.reclassifyPerformer(editId || edit.id, {
        performerId,
        newType: newType as unknown as TEditReviewPerformerType,
        reason,
      });

      if (response?.editReview) {
        setEditReview(response.editReview);
        toast.success(`Successfully reclassified performer as ${newType}`);
      }
    } catch (error) {
      console.error('Error reclassifying performer:', error);
      toast.error('Failed to reclassify performer');
    } finally {
      setProcessingAction(null);
    }
  };

  const handleMarkInvalid = async (editId: string, performerId: string, reason?: string) => {
    if (!editId && !edit.id) return;

    setProcessingAction(performerId);
    try {
      const response = await EditReviewService.markPerformerInvalid(editId || edit.id, {
        performerId,
        reason,
      });

      if (response?.editReview) {
        setEditReview(response.editReview);
        toast.success('Successfully marked as invalid detection');
      }
    } catch (error) {
      console.error('Error marking performer invalid:', error);
      toast.error('Failed to mark as invalid');
    } finally {
      setProcessingAction(null);
    }
  };

  const handleOutgrade = async (
    editId: string,
    performerId: string,
    contractedType: RolePerformerType,
    reason?: string
  ) => {
    if (!editId && !edit.id) return;

    setProcessingAction(performerId);
    try {
      const response = await EditReviewService.outgradePerformer(editId || edit.id, {
        performerId,
        contractedType: contractedType as unknown as TEditReviewPerformerType,
        reason,
      });

      if (response?.editReview) {
        setEditReview(response.editReview);
        const analysis = response.editReview.analyses?.find((a) => a.performerId === performerId);
        toast.success(analysis?.outgraded ? 'Performer outgraded - not in this edit' : 'Outgrade removed');
      }
    } catch (error) {
      console.error('Error outgrading performer:', error);
      toast.error('Failed to outgrade performer');
    } finally {
      setProcessingAction(null);
    }
  };

  const togglePerformerSelection = (performerId: string) => {
    const newSelection = new Set(selectedPerformers);
    if (newSelection.has(performerId)) {
      newSelection.delete(performerId);
    } else {
      newSelection.add(performerId);
    }
    setSelectedPerformers(newSelection);
  };

  const handleMergePerformers = async (editId?: string) => {
    if ((!editId && !edit.id) || selectedPerformers.size < 2) return;

    setProcessingAction('merge');
    try {
      const response = await EditReviewService.mergePerformers(editId || edit.id, {
        performerIds: Array.from(selectedPerformers),
        newName: mergeName || undefined,
      });

      if (response?.editReview) {
        setEditReview(response.editReview);
        setSelectedPerformers(new Set());
        setShowMergeDialog(false);
        setMergeName('');
        toast.success(`Successfully merged ${selectedPerformers.size} performers`);
      }
    } catch (error) {
      console.error('Error merging performers:', error);
      toast.error('Failed to merge performers');
    } finally {
      setProcessingAction(null);
    }
  };

  const handleIdentifyPerformer = async (unknownPerformerId: string, contractedPerformerId: string) => {
    if (!edit.id) return;

    setProcessingAction(unknownPerformerId);
    try {
      const response = await EditReviewService.identifyPerformer(edit.id, {
        unknownPerformerId,
        contractedPerformerId,
      });

      if (response?.editReview) {
        setEditReview(response.editReview);
        toast.success('Successfully identified performer');
      }
    } catch (error) {
      console.error('Error identifying performer:', error);
      toast.error('Failed to identify performer');
    } finally {
      setProcessingAction(null);
    }
  };

  /** Effects **/

  useEffect(() => {
    const loadData = async () => {
      if (commercial && edit) {
        setLoading(true);
        const c = await performerStore.listPerformerContracts({
          commercialId: commercial.id,
          relations: 'performer,role',
        });
        const e = await commercialStore.getEditReviewByEditId(edit.id);

        if (!c) {
          toast.error('Failed to load contracts');
          setLoading(false);
          return;
        } else {
          setContracts(c);
        }

        if (!e?.editReview) {
          // toast.error('Failed to load edit review');
          // fail silently in case this edit review wasn't generated yet
          setLoading(false);
          return;
        } else {
          setEditReview(e.editReview);
        }
      }
      setLoading(false);
    };
    loadData();
  }, [commercial, edit, setShowEditReviewModal]);

  useEffect(() => {
    if (contracts.length > 0 && editReview) {
      const knownPerformerIds = new Set(contracts.map((c) => c.performer.id));

      // Filter analyses into known (contracted) and unknown performers
      const known = contracts.map((contract) => {
        const analysis = editReview.analyses?.find((a) => a.performerId === contract.performer.id);

        // Check if performer is outgraded
        if (analysis?.outgraded) {
          return { contract, analysis, frameUrls: [], verdict: Verdict.outgraded };
        }

        // the performer's face wasn't detected in the analysis (or was outgraded with no actual detections)
        if (!analysis || analysis.frameAppears.length === 0) {
          if (contract.role.performer_type === RolePerformerType.Extra) {
            return { contract, analysis, frameUrls: [], verdict: Verdict.noChange };
          } else if (contract.role.performer_type === RolePerformerType.OnCameraPrincipal) {
            return { contract, analysis, frameUrls: [], verdict: Verdict.downgrade };
          } else {
            console.log('Unexpected performer type:', contract.role.performer_type);
            return { contract, analysis, frameUrls: [], verdict: Verdict.noChange };
          }
        }

        // the performer's face was detected in the analysis
        // Get frames for the analysis
        const frameUrls = editReview.framePaths
          ?.filter((imageLocation) =>
            analysis.frameAppears.some((frameIndex) =>
              imageLocation.fileName.includes(`${String(frameIndex).padStart(4, '0')}.jpg`)
            )
          )
          .map((imageLocation) => imageLocation.signedUrl);

        // Determine the verdict based on the analysis type
        if (contract.role.performer_type === RolePerformerType.Extra) {
          if (analysis.type === RolePerformerType.Extra) {
            return { contract, analysis, frameUrls, verdict: Verdict.noChange };
          } else if (analysis.type === RolePerformerType.OnCameraPrincipal) {
            return { contract, analysis, frameUrls, verdict: Verdict.upgrade };
          } else {
            console.log('Unexpected analysis type for EXTRA performer:', analysis.type);
            return { contract, analysis, frameUrls, verdict: Verdict.noChange };
          }
        } else if (contract.role.performer_type === RolePerformerType.OnCameraPrincipal) {
          if (analysis.type === RolePerformerType.OnCameraPrincipal) {
            return { contract, analysis, frameUrls, verdict: Verdict.noChange };
          } else if (analysis.type === RolePerformerType.Extra) {
            return { contract, analysis, frameUrls, verdict: Verdict.downgrade };
          } else {
            console.log('Unexpected analysis type for ON_CAMERA_PRINCIPAL performer:', analysis.type);
            return { contract, analysis, frameUrls, verdict: Verdict.noChange };
          }
        } else {
          console.log('Unexpected performer type:', contract.role.performer_type);
          return { contract, analysis, frameUrls, verdict: Verdict.noChange };
        }
      });

      // Find unknown performers (not in contracts) - include invalid ones to show them grayed out
      const unknown = (editReview.analyses || [])
        .filter((analysis) => !knownPerformerIds.has(analysis.performerId))
        .map((analysis) => {
          const frameUrls = editReview.framePaths
            ?.filter((imageLocation) =>
              analysis.frameAppears.some((frameIndex) =>
                imageLocation.fileName.includes(`${String(frameIndex).padStart(4, '0')}.jpg`)
              )
            )
            .map((imageLocation) => imageLocation.signedUrl);

          return { analysis, frameUrls };
        });

      setKnownAnalyses(known);
      setUnknownAnalyses(unknown);
    }
  }, [contracts, editReview]);

  /** Render **/

  return (
    <Layer
      onEsc={() => setShowEditReviewModal(false)}
      onClickOutside={() => setShowEditReviewModal(false)}
      position="center"
      modal
      background="transparent"
    >
      <ModalCard
        onClose={() => setShowEditReviewModal(false)}
        title="Edit Review"
        noLayerComponent
        cardProps={{ width: '845px' }}
      >
        <CardHeader justify="end" direction="row" gap="small" pad="small">
          <Button
            icon={editReview ? <Update size="20px" /> : <Magic size="20px" />}
            hoverIndicator
            label={editReview ? 'Rerun AI Edit Review' : 'Start AI Edit Review'}
            onClick={() => createEditReview()}
            primary={!editReview}
            secondary={!!editReview}
          />
        </CardHeader>
        <CardBody gap="large">
          {loading && !editReview && <Loader />}

          {/* Contracted Performers Section */}
          {knownAnalyses.length > 0 && (
            <ContractedPerformersPanel
              knownAnalyses={knownAnalyses}
              editReview={editReview}
              processingAction={processingAction}
              setEnlargedFrameUrl={setEnlargedFrameUrl}
              handleOutgrade={handleOutgrade}
              handleReclassify={handleReclassify}
              handleMarkInvalid={handleMarkInvalid}
            />
          )}

          {/* Unknown Performers Section */}
          {unknownAnalyses.length > 0 && (
            <UnknownPerformersPanel
              unknownAnalyses={unknownAnalyses}
              contracts={contracts}
              selectedPerformers={selectedPerformers}
              processingAction={processingAction}
              setEnlargedFrameUrl={setEnlargedFrameUrl}
              setShowMergeDialog={setShowMergeDialog}
              togglePerformerSelection={togglePerformerSelection}
              handleIdentifyPerformer={handleIdentifyPerformer}
              handleReclassify={handleReclassify}
              handleMarkInvalid={handleMarkInvalid}
            />
          )}
        </CardBody>
      </ModalCard>

      {/* Enlarged Frame Popup */}
      {enlargedFrameUrl && (
        <EnlargedFrameModal enlargedFrameUrl={enlargedFrameUrl} setEnlargedFrameUrl={setEnlargedFrameUrl} />
      )}

      {/* Merge Dialog */}
      {showMergeDialog && (
        <MergeDialogModal
          unknownAnalyses={unknownAnalyses}
          selectedPerformers={selectedPerformers}
          processingAction={processingAction}
          enlargedFrameUrl={enlargedFrameUrl}
          mergeName={mergeName}
          setMergeName={setMergeName}
          setShowMergeDialog={setShowMergeDialog}
          handleMergePerformers={handleMergePerformers}
        />
      )}
    </Layer>
  );
});
